import React from 'react';
import { t, useUiLang } from '../utils/lang.js';

function formatTime(sec) {
  if (!Number.isFinite(sec)) return '00:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function AudioPlayer({
  src,
  isPlaying,
  isBuffering,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  onClose,
  onDownload,
  onChangeReciter,
  reciter,
  availableReciters = {},
  playbackRate = 1,
  onChangePlaybackRate,
  volume = 1,
  onVolumeChange,
  title = '',
  subtitle = '',
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false
}) {
  const uiLang = useUiLang();
  const isAr = uiLang === 'ar';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [speedMenuOpen, setSpeedMenuOpen] = React.useState(false);
  const [reciterMenuOpen, setReciterMenuOpen] = React.useState(false);
  const [volumeOpen, setVolumeOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const speedRef = React.useRef(null);
  const reciterRef = React.useRef(null);
  const volumeRef = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (speedRef.current && !speedRef.current.contains(e.target)) setSpeedMenuOpen(false);
      if (reciterRef.current && !reciterRef.current.contains(e.target)) setReciterMenuOpen(false);
      if (volumeRef.current && !volumeRef.current.contains(e.target)) setVolumeOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const progress = (duration && duration > 0) ? (currentTime / duration) * 100 : 0;

  const speedOptions = [
    { label: '0,25x', value: 0.25 },
    { label: '0,5x', value: 0.5 },
    { label: '0,75x', value: 0.75 },
    { label: '1x (Обычный)', value: 1 },
    { label: '1,25x', value: 1.25 },
    { label: '1,5x', value: 1.5 },
    { label: '1,75x', value: 1.75 },
    { label: '2x', value: 2 }
  ];

  return (
    <div className="audio-player" style={{ direction: 'ltr' }}>
      <div className="audio-player__header">
        <div className="audio-player__info">
          <div className="audio-player__title">{title}</div>
        </div>
        {subtitle && <div className="audio-player__subtitle">{subtitle}</div>}
      </div>

      <div className="audio-player__progress-wrapper">
        <input
          type="range"
          min="0"
          max="100"
          value={progress || 0}
          className="audio-player__progress"
          onChange={(e) => {
            if (duration) onSeek((parseFloat(e.target.value) / 100) * duration);
          }}
        />
        <div className="audio-player__progress-fill" style={{ width: `${progress}%`, transition: isPlaying ? 'width 120ms linear' : 'none' }} />
      </div>

      <div className="audio-player__controls">
        <div className="audio-player__time">{formatTime(currentTime)}</div>

        <div className="audio-player__buttons">
          <div className="audio-player__btn-wrapper" ref={menuRef}>
            <button className="audio-player__btn" onClick={() => setMenuOpen(o => !o)} title={t('menu')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
              </svg>
            </button>
            {menuOpen && (
              <div className="audio-player__dropdown">
                <button className="audio-player__dropdown-item" onClick={() => { setMenuOpen(false); if (onDownload) onDownload(); }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {t('download')}
                </button>
                <button className="audio-player__dropdown-item" onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  setTimeout(() => setSpeedMenuOpen(true), 0);
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                  {t('speed')}
                </button>
                {onChangeReciter && (
                  <button className="audio-player__dropdown-item" onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    setTimeout(() => setReciterMenuOpen(true), 0);
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                    {t('reciter')}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="audio-player__btn-wrapper" ref={volumeRef}>
            <button className="audio-player__btn" onClick={() => setVolumeOpen(o => !o)} title={t('volume')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            </button>
            {volumeOpen && (
              <div className="audio-player__volume-slider">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume * 100}
                  orient="vertical"
                  onChange={(e) => { if (onVolumeChange) onVolumeChange(parseFloat(e.target.value) / 100); }}
                />
              </div>
            )}
          </div>

          <button
            className="audio-player__btn"
            onClick={onPrev}
            disabled={!hasPrev || !!isBuffering}
            title={t('prev_ayah')}
            style={{ opacity: (hasPrev && !isBuffering) ? 1 : 0.3, cursor: (hasPrev && !isBuffering) ? 'pointer' : 'not-allowed' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ transform: isAr ? 'scaleX(-1)' : 'none' }}>
              <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
            </svg>
          </button>

          <button
            className="audio-player__btn audio-player__btn--play"
            onClick={onPlayPause}
            disabled={!!isBuffering}
            title={isBuffering ? 'Загрузка…' : (isPlaying ? t('pause') : t('play'))}
            style={{ opacity: isBuffering ? 0.6 : 1, cursor: isBuffering ? 'not-allowed' : 'pointer' }}
          >
            {isBuffering ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="9" strokeDasharray="14 14">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                </circle>
              </svg>
            ) : isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            className="audio-player__btn"
            onClick={onNext}
            disabled={!hasNext || !!isBuffering}
            title={t('next_ayah')}
            style={{ opacity: (hasNext && !isBuffering) ? 1 : 0.3, cursor: (hasNext && !isBuffering) ? 'pointer' : 'not-allowed' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ transform: isAr ? 'scaleX(-1)' : 'none' }}>
              <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
            </svg>
          </button>

          <button className="audio-player__btn" onClick={onClose} title={t('close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="audio-player__time">{formatTime(duration)}</div>
      </div>

      {speedMenuOpen && (
        <div className="audio-player__speed-dropdown" ref={speedRef}>
          <div className="audio-player__dropdown-header">
            <button className="audio-player__dropdown-back" onClick={(e) => {
              e.stopPropagation();
              setSpeedMenuOpen(false);
              setTimeout(() => setMenuOpen(true), 0);
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
              </svg>
              <span>{t('speed')}</span>
            </button>
          </div>
          {speedOptions.map(opt => (
            <button
              key={opt.value}
              className={`audio-player__dropdown-item ${playbackRate === opt.value ? 'active' : ''}`}
              onClick={() => { if (onChangePlaybackRate) onChangePlaybackRate(opt.value); }}
            >
              {playbackRate === opt.value && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              <span>{opt.label.replace('(Обычный)', `(${t('normal')})`)}</span>
            </button>
          ))}
        </div>
      )}

      {reciterMenuOpen && (
        <div className="audio-player__speed-dropdown" ref={reciterRef}>
          <div className="audio-player__dropdown-header">
            <button className="audio-player__dropdown-back" onClick={(e) => {
              e.stopPropagation();
              setReciterMenuOpen(false);
              setTimeout(() => setMenuOpen(true), 0);
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
              </svg>
              <span>{t('reciter_menu')}</span>
            </button>
          </div>
          {(() => {
            const reciters = Object.keys(availableReciters);
            const withHighlight = reciters.filter(k => availableReciters[k].wordHighlight !== false);
            const withoutHighlight = reciters.filter(k => availableReciters[k].wordHighlight === false);

            return (
              <>
                {withHighlight.length > 0 && (
                  <>
                    <div className="audio-player__dropdown-section">С подсветкой слов</div>
                    {withHighlight.map(reciterKey => (
                      <button
                        key={reciterKey}
                        className={`audio-player__dropdown-item ${reciter === reciterKey ? 'active' : ''}`}
                        onClick={() => { if (onChangeReciter) onChangeReciter(reciterKey); setReciterMenuOpen(false); }}
                      >
                        {reciter === reciterKey && (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        <span className="reciter-name">{availableReciters[reciterKey].name}</span>
                      </button>
                    ))}
                  </>
                )}

                {withoutHighlight.length > 0 && (
                  <>
                    <div className="audio-player__dropdown-section audio-player__dropdown-section--no-highlight">Без подсветки слов</div>
                    {withoutHighlight.map(reciterKey => (
                      <button
                        key={reciterKey}
                        className={`audio-player__dropdown-item ${reciter === reciterKey ? 'active' : ''}`}
                        onClick={() => { if (onChangeReciter) onChangeReciter(reciterKey); setReciterMenuOpen(false); }}
                      >
                        {reciter === reciterKey && (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        <span className="reciter-name">{availableReciters[reciterKey].name}</span>
                      </button>
                    ))}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
